import React from "react";
import {
  Table,
  Pagination,
  Avatar,
  Dropdown,
  Button,
  Tag,
  Modal,
  message,
  Switch,
} from "antd";
import {
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { Product } from "../../hooks/useProducts";

interface InventoryTableProps {
  data: Product[];
  meta: { total: number; current_page: number; last_page: number };
  onModifyProduct: (product: Product) => void;
  onDeleteProduct: (productId: number) => void;
  onToggleAvailability: (productId: number, currentStatus: number) => void;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  canModifyInventory: boolean;
  canDeleteInventory: boolean;
}

const InventoryTable: React.FC<InventoryTableProps> = ({
  data,
  meta,
  onModifyProduct,
  onDeleteProduct,
  onToggleAvailability,
  isLoading,
  onPageChange,
  canModifyInventory,
  canDeleteInventory,
}) => {
  const handleDelete = (productId: number) => {
    if (!canDeleteInventory) {
      message.error("You don't have permission to delete products");
      return;
    }

    Modal.confirm({
      title: "Delete Product",
      content: "Are you sure you want to delete this product?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => onDeleteProduct(productId),
    });
  };

  const handleModify = (product: Product) => {
    if (!canModifyInventory) {
      message.error("You don't have permission to modify products");
      return;
    }

    onModifyProduct(product);
  };

  const handleAvailabilityToggle = (
    productId: number,
    currentStatus: number,
    currentQty: number
  ) => {
    if (!canModifyInventory) {
      message.error("You don't have permission to modify products");
      return;
    }

    // Prevent making product available if stock is 0
    if (currentQty === 0 && currentStatus === 0) {
      message.warning("Cannot make product available when stock is 0");
      return;
    }

    onToggleAvailability(productId, currentStatus);
  };

  // Create columns array without the action column
  const baseColumns: ColumnsType<Product> = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div className="flex items-center space-x-2">
          <Avatar src={record.images?.[0]?.url || "/placeholder.png"} />
          <span>{name}</span>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => category?.name || "Unknown",
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      render: (vendor) => vendor?.name || "Unknown",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location) => location?.name || "Unknown",
    },
    {
      title: "Stock",
      dataIndex: "qty",
      key: "stock",
      render: (qty) => (
        <Tag color={qty <= 10 ? "red" : "green"}>
          {qty <= 10 ? "Low Stock " : "In Stock "} {`(${qty})`}
        </Tag>
      ),
    },
    {
      title: "Price",
      dataIndex: "amount",
      key: "price",
      render: (amount) => `₦${amount}`,
    },
    {
      title: "Availability",
      dataIndex: "status",
      key: "availability",
      render: (status, record) => (
        <div className="flex items-center space-x-2">
          <Switch
            checked={status === 1 && record.qty > 0} // Auto-disable if qty is 0
            disabled={!canModifyInventory || record.qty === 0}
            onChange={() =>
              handleAvailabilityToggle(record.id, status, record.qty)
            }
            checkedChildren="Available"
            unCheckedChildren="Unavailable"
            size="small"
          />
          {record.qty === 0 && (
            <span className="text-xs text-red-500">Out of stock</span>
          )}
        </div>
      ),
    },
  ];

  // Only add action column if user has modify or delete permissions
  const columns = [...baseColumns];

  if (canModifyInventory || canDeleteInventory) {
    columns.push({
      title: "Action",
      key: "action",
      render: (_, record) => {
        // Build menu items based on permissions
        const menuItems = [];

        if (canModifyInventory) {
          menuItems.push({
            key: "modify",
            label: "Modify",
            icon: <EditOutlined />,
            onClick: () => handleModify(record),
          });
        }

        if (canDeleteInventory) {
          menuItems.push({
            key: "delete",
            label: "Delete",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => handleDelete(record.id),
          });
        }

        // Only render dropdown if there are menu items
        return menuItems.length > 0 ? (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        ) : null;
      },
    });
  }

  return (
    <div>
      <Table
        loading={isLoading}
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={{ x: "max-content" }}
        rowKey="id"
      />
      <div className="flex justify-end mt-4">
        <Pagination
          current={meta.current_page}
          total={meta.total}
          onChange={onPageChange}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};

export default InventoryTable;
